import React, { useState, useCallback, useContext, ComponentProps, useEffect, useMemo } from 'react'
import { trackAutosuggestionClick, trackAutosuggestionsResult } from 'api/interactionStudio'
import GeoContext from 'contexts/geoContext'
import { GlobalSearchDispatchContext, GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import noop from 'lib/function/noop'
import uuidV4 from 'lib/string/uuidV4Utils'
import SearchPlaceSelect from 'components/Search/SearchPlaceSelect/SearchPlaceSelect'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { fetchExperienceById } from 'actions/ExperienceActions'
import { getCurrentUserId } from 'selectors/accountSelectors'
import { lereRecommendedDestinationsToTrendingDestinations } from 'lib/customer/recommendationUtils'
import { ANYWHERE_PLACE_ID, DESTINATION_MODEL_ELIGIBLE_REGIONS } from 'constants/search'
import { TypeaheadActionType, typeaheadEventWithContext, typeaheadQuery } from 'analytics/snowplow/events'
import { convertSearchItemTypeToTypeaheadItemType } from 'analytics/snowplow/helpers'
import * as Analytics from 'analytics/analytics'
import usePrevious from 'hooks/usePrevious'
import { destinationOverwrite } from 'constants/config/lebtOverwrite'

const SEARCH_BAR_DESTINATIONS_LIMIT = 6
const SEARCH_BAR_BUSINESS_OFFICES_LIMIT = 6
const MINIMUM_NUM_DESTINATIONS = 6

interface Props {
  placeTypes?: Array<App.SearchPlaceType>;
  searchTypes?: Array<App.SearchType>;
  placeholder?: string;
  onChange?: (item?: App.SearchItem) => void;
  className?: string;
  label?: string;
  variant: 'inline' | 'dropdown';
  dropdownSize?: ComponentProps<typeof SearchPlaceSelect>['dropdownSize']
  inputRef?: React.Ref<HTMLInputElement>;
  controlsContainerRef?: React.RefObject<HTMLDivElement | null>;
  /** Whether or not this search input is for the secondary location on a search */
  isSecondaryLocation?: boolean;
  /** Allow to provide your own set of placeholder items (often 'popular places') instead of taking from state */
  placeholderItems?: Array<App.SearchItem>;
  /** A list of recent search items, will be displayed along with placeholder items */
  recentSearchItems?: Array<App.FullRecentSearch>;
  /** Use this to supply your own search results list the input will use */
  overrideSearchItems?: Array<App.SearchItem>;
  required?: boolean;
  searchVertical?: App.SearchPlaceVertical;
  onLocationToggle?: (e?: React.MouseEvent<HTMLButtonElement>) => void;
}

/**
 * Common search control for setting search for a 'place'
 * Is already hard wired into the existing global search context
 * and will populate the 'searchItem' property
 */
function SearchLocationInput(props: Props) {
  const {
    searchTypes,
    placeholder,
    onChange = noop,
    className,
    label,
    placeTypes,
    variant,
    dropdownSize,
    inputRef,
    isSecondaryLocation,
    placeholderItems,
    required,
    searchVertical,
    recentSearchItems,
    overrideSearchItems,
    onLocationToggle,
    controlsContainerRef,
  } = props
  const dispatch = useAppDispatch()
  const [searchSuggestionSessionId, setSearchSuggestionSessionId] = useState<string>('')
  const [searchSessionId, setSearchSessionId] = useState<string | undefined>(undefined)
  const [searchPhrase, setSearchPhrase] = useState<string>('')
  const [currentSuggestions, setCurrentSuggestions] = useState<Array<App.SearchItem>>([])
  const { currentRegionCode } = useContext(GeoContext)
  const searchState = useContext(GlobalSearchStateContext)
  const { searchVerticals, popularDestinations } = searchState
  const searchItem = isSecondaryLocation ? searchState.secondarySearchItem : searchState.searchItem
  const searchDispatch = useContext(GlobalSearchDispatchContext)
  const domainUserId = useAppSelector(state => state.auth.domainUserId)
  const leUserId = useAppSelector(getCurrentUserId)
  const recommendedDestinations = useAppSelector(state => state.recommendations.recommendedDestinations)

  const isEligibleRegion = DESTINATION_MODEL_ELIGIBLE_REGIONS.includes(currentRegionCode)
  const hasUserId = !!domainUserId || !!leUserId
  const hotelSearchSelected = searchVertical === 'hotel'
  const hasRecommendations = recommendedDestinations.state !== 'loading' && recommendedDestinations.destinations.length > 0
  const shouldShowRecommendations = isEligibleRegion && hasUserId && hotelSearchSelected && hasRecommendations
  const personalisedRecommendations = lereRecommendedDestinationsToTrendingDestinations(recommendedDestinations.destinations).slice(0, SEARCH_BAR_DESTINATIONS_LIMIT)
  const personalisedRecsSearchItem: Array<App.SearchItem> = personalisedRecommendations.map(rec => ({
    value: rec.destinationId,
    format: {
      mainText: rec.name,
    },
    imageId: rec.imageId,
    searchType: 'destination',
  } as App.SearchDestination))

  const businessOffices = useAppSelector(state => state.businessTraveller.businessOffices)
  const hasBusinessOffices = businessOffices?.status === 'success' && businessOffices.all && Object.keys(businessOffices.all).length > 0

  // Full list is used so the typing-flow can match against every office.
  const businessOfficeItems: Array<App.SearchItem> = useMemo(() =>
    searchVertical === 'hotel' && hasBusinessOffices && businessOffices?.status === 'success' ? Object.values(businessOffices.all).map(office => ({
      value: `${office.latitude},${office.longitude}`,
      latLongName: office.name,
      lat: office.latitude,
      lng: office.longitude,
      format: {
        mainText: office.name,
        secondaryText: [office.address.mainText, office.address.secondaryText].filter(Boolean).join(', '),
      },
      searchType: 'lat_long',
    })) : [],
  [hasBusinessOffices, businessOffices, searchVertical]
  )

  // Displayed subset is rendered before the user types.
  const displayedBusinessOfficeItems = useMemo(
    () => businessOfficeItems.slice(0, SEARCH_BAR_BUSINESS_OFFICES_LIMIT),
    [businessOfficeItems],
  )

  const popularItems = ((shouldShowRecommendations && personalisedRecsSearchItem.length >= MINIMUM_NUM_DESTINATIONS) ? personalisedRecsSearchItem : (placeholderItems ?? popularDestinations)).map(item => ({
    ...item,
    value: destinationOverwrite[item.value ?? ''] ?? item.value,
  }))
  const filteredPopularItems = popularItems.filter(item => item.value !== ANYWHERE_PLACE_ID).slice(0, SEARCH_BAR_DESTINATIONS_LIMIT)

  const onLocationSelect = useCallback((
    searchItem?: App.SearchItem,
    searchPhrase: string = '',
    actionType?: TypeaheadActionType,
    searchItemPosition?: App.SearchItemPosition,
  ) => {
    if (!searchItem) {
      searchDispatch({
        type: isSecondaryLocation ? GlobalSearchStateActions.UNSET_SECONDARY_SEARCH_ITEM : GlobalSearchStateActions.UNSET_SEARCH_ITEM,
      })
    } else {
      if (searchItem.searchType === 'experience') {
        dispatch(fetchExperienceById(searchItem.value))
      }

      searchDispatch({
        type: isSecondaryLocation ? GlobalSearchStateActions.SET_SECONDARY_SEARCH_ITEM : GlobalSearchStateActions.SET_SEARCH_ITEM,
        searchItem,
      })

      const showSuggestions = searchPhrase && searchPhrase !== searchState.searchItem?.format.mainText
      const clickedSuggestionIndex =
        showSuggestions ? currentSuggestions.findIndex(item => item.value === searchItem.value) : -1
      const popularItemsIndex =
        [...displayedBusinessOfficeItems, ...filteredPopularItems].findIndex(item => item.value === searchItem.value)
      const isPopularPlace = !showSuggestions

      let newSearchSessionId = undefined
      if (!searchSessionId) {
        newSearchSessionId = uuidV4()
        setSearchSessionId(newSearchSessionId)
      }

      Analytics.trackEvent(
        typeaheadEventWithContext({
          actionName: actionType ?? 'select',
          searchSessionId: newSearchSessionId ?? searchSessionId ?? uuidV4(),
          typeaheadItem: {
            positionOrdinal:
              (isPopularPlace ? popularItemsIndex : clickedSuggestionIndex) + 1, // Plus one because 1 based index
            itemId: searchItem.value,
            itemType: convertSearchItemTypeToTypeaheadItemType(searchItem.searchType),
            tileType: isPopularPlace ? 'popular' : 'searchResult',
            hasImage:
              searchItem.searchType === 'property' ? true : (!!searchItem.format.imageId || !!searchItem.imageId),
            positionX: searchItemPosition?.x ?? undefined,
            positionY: searchItemPosition?.y ?? undefined,
            numRows: searchItemPosition?.numRows ?? undefined,
            numCols: searchItemPosition?.numCols ?? undefined,
          },
        }))

      trackAutosuggestionClick({
        region: currentRegionCode,
        verticals: searchVerticals,
        searchSuggestionSessionId,
        searchPhrase,
        clickedSuggestionIndex,
        clickedSuggestion: searchItem,
      })
    }
    onChange(searchItem)
  }, [onChange, searchDispatch, isSecondaryLocation, searchState.searchItem?.format.mainText, currentSuggestions, displayedBusinessOfficeItems, filteredPopularItems, searchSessionId, currentRegionCode, searchVerticals, searchSuggestionSessionId, dispatch])

  const onResultsChange = useCallback((results: Array<App.SearchItem>, searchPhrase: string, numColumns?: number) => {
    setCurrentSuggestions(results)
    searchDispatch({
      type: GlobalSearchStateActions.SET_SUGGESTED_SEARCH_ITEMS,
      searchItems: results,
    })
    const newSearchSuggestionSessionId = uuidV4()
    setSearchSuggestionSessionId(newSearchSuggestionSessionId)

    let newSearchSessionId = undefined
    if (!searchSessionId) {
      newSearchSessionId = uuidV4()
      setSearchSessionId(newSearchSessionId)
    }

    if (searchPhrase) {
      Analytics.trackEvent(
        typeaheadQuery({
          userQuery: searchPhrase,
          searchSessionId: newSearchSessionId ?? searchSessionId ?? uuidV4(),
          typeaheadItems: results.map((searchItem, index) => ({
            positionOrdinal: index + 1, // Plus one because 1 based index
            itemId: searchItem.value,
            itemType: convertSearchItemTypeToTypeaheadItemType(searchItem.searchType),
            tileType: 'searchResult',
            hasImage:
              searchItem.searchType === 'property' ? true : (!!searchItem.format.imageId || !!searchItem.imageId),
            positionX: numColumns ? index % numColumns : undefined,
            positionY: numColumns ? Math.floor(index / numColumns) : undefined,
            numRows: numColumns ? Math.ceil(results.length / numColumns) : undefined,
            numCols: numColumns,
          })),
        }),
      )
    }

    trackAutosuggestionsResult({
      region: currentRegionCode,
      verticals: searchVerticals,
      searchSuggestionSessionId: newSearchSuggestionSessionId,
      searchPhrase,
      suggestions: results,
    })
  }, [searchDispatch, searchSessionId, currentRegionCode, searchVerticals])

  const handleClear = useCallback(() => {
    setSearchSessionId(undefined)
  }, [])

  const previousSearchPhrase = usePrevious(searchPhrase)

  // When the first character of the search string changes, we want to reset the search sessionId
  useEffect(() => {
    if (!previousSearchPhrase || !searchPhrase) return

    const isFirstCharSame = previousSearchPhrase[0] === searchPhrase[0]
    const isSingleChar = searchPhrase.length === 1

    if (!isFirstCharSame && isSingleChar) {
      setSearchSessionId(undefined)
    }
  }, [searchPhrase, previousSearchPhrase])

  const handleSearchPhraseChange = useCallback((searchPhrase: string) => {
    setSearchPhrase(searchPhrase)
  }, [])

  return <SearchPlaceSelect
    className={className}
    label={label}
    placeholder={placeholder}
    value={searchItem}
    searchTypes={searchTypes}
    placeTypes={placeTypes}
    onChange={onLocationSelect}
    onToggle={onLocationToggle}
    onResultsChange={onResultsChange}
    placeholderItems={filteredPopularItems}
    placeholderTitle="Popular places"
    businessOfficeItems={businessOfficeItems}
    displayedBusinessOfficeItems={displayedBusinessOfficeItems}
    businessOfficeTitle="Business offices"
    variant={variant}
    dropdownSize={dropdownSize}
    inputRef={inputRef}
    required={required}
    searchVertical={searchVertical}
    recentSearchItems={recentSearchItems}
    overrideSearchItems={overrideSearchItems}
    controlsContainerRef={controlsContainerRef}
    onClear={handleClear}
    onPhraseChange={handleSearchPhraseChange}
  />
}

export default SearchLocationInput
